/*
 * VARELON AIR | MVEC Airport Picker v1.0
 * Read-only airport suggestions using the existing OurAirports catalogue.
 * No pricing, calculation, quotation, account, or server-side changes.
 * Load AFTER varelon-airports-global.js on the MVEC page only.
 */
(() => {
  'use strict';

  const COPY = {
    en: ['Airport suggestions', 'No matching airports', 'Geographic suggestions only; suitability for private charter requires operator confirmation.'],
    es: ['Sugerencias de aeropuertos', 'No se encontraron aeropuertos', 'Sugerencias geográficas; la idoneidad para chárter privado requiere confirmación del operador.'],
    fr: ["Suggestions d’aéroports", 'Aucun aéroport trouvé', "Suggestions géographiques uniquement ; la faisabilité d’un charter privé doit être confirmée par l’opérateur."],
    de: ['Flughafenvorschläge', 'Keine Flughäfen gefunden', 'Nur geografische Vorschläge; die Eignung für einen Privat charter muss vom Betreiber bestätigt werden.'],
    it: ['Suggerimenti aeroporti', 'Nessun aeroporto trovato', "Solo suggerimenti geografici; l’idoneità al charter privato richiede conferma dell’operatore."],
    pt: ['Sugestões de aeroportos', 'Nenhum aeroporto encontrado', 'Sugestões geográficas; a adequação para charter privado requer confirmação do operador.'],
    ja: ['空港の候補', '該当する空港が見つかりません', '地理的な候補です。プライベートチャーターの運航可否は運航会社による確認が必要です。']
  };

  const STYLE_ID = 'va-mvec-airport-picker-styles';
  const OPEN_LISTS = new Set();
  let serial = 0;

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .va-mvec-airport-menu {
        position: fixed;
        z-index: 2147483000;
        box-sizing: border-box;
        margin: 0;
        padding: 5px;
        background: #fff;
        color: #102034;
        border: 1px solid #c8a96b;
        border-radius: 9px;
        box-shadow: 0 16px 42px rgba(6, 19, 33, .23);
        max-height: min(340px, 48vh);
        overflow-y: auto;
        overscroll-behavior: contain;
        font: 13px/1.5 Arial, Helvetica, sans-serif;
      }
      .va-mvec-airport-menu[hidden] { display: none !important; }
      .va-mvec-airport-option {
        display: block;
        width: 100%;
        margin: 0;
        padding: 10px 12px;
        background: transparent;
        color: #102034;
        border: 0;
        border-radius: 5px;
        text-align: left;
        cursor: pointer;
        font: inherit;
        white-space: normal;
      }
      .va-mvec-airport-option:hover,
      .va-mvec-airport-option.is-active,
      .va-mvec-airport-option:focus-visible {
        background: #f4ead4;
        outline: none;
      }
      .va-mvec-airport-primary { display: block; font-weight: 700; }
      .va-mvec-airport-secondary { display: block; color: #536477; font-size: 11px; }
      .va-mvec-airport-info {
        padding: 9px 12px;
        border-top: 1px solid #e6e9ed;
        color: #59697a;
        font-size: 11px;
        line-height: 1.5;
      }
      .va-mvec-airport-empty { padding: 12px; color: #536477; }
    `;
    document.head.appendChild(style);
  }

  function regionLanguage(root) {
    const locale = String(root.getAttribute('data-locale') || document.documentElement.lang || 'en');
    const primary = locale.toLowerCase().split(/[-_]/)[0];
    return COPY[primary] || COPY.en;
  }

  function airportLabel(record, limit) {
    const city = String(record.city || '').trim();
    const airport = String(record.airport || '').trim();
    const code = String(record.iata || record.icao || '').trim().toUpperCase();
    const base = city && airport && city.toLocaleLowerCase() !== airport.toLocaleLowerCase()
      ? `${city} — ${airport}` : (airport || city);
    const suffix = code ? ` (${code})` : '';
    const max = Number.isFinite(limit) && limit > 0 ? limit : 70;
    return base.slice(0, Math.max(0, max - suffix.length)).trimEnd() + suffix;
  }

  function setup(input, root, catalog) {
    if (input.dataset.mvecAirportPickerReady === '1') return;
    input.dataset.mvecAirportPickerReady = '1';
    const copy = regionLanguage(root);
    const id = `va-mvec-airports-${++serial}`;
    const menu = document.createElement('div');
    menu.className = 'va-mvec-airport-menu';
    menu.id = id;
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', copy[0]);
    menu.hidden = true;
    document.body.appendChild(menu);
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', id);
    input.setAttribute('aria-expanded', 'false');

    let choices = [];
    let active = -1;
    let isSelecting = false;
    let optionButtons = [];

    function close() {
      menu.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      OPEN_LISTS.delete(close);
      active = -1;
    }

    function place() {
      if (menu.hidden) return;
      const rect = input.getBoundingClientRect();
      if (rect.width < 25 || rect.bottom < 0 || rect.top > window.innerHeight) {
        close();
        return;
      }
      const width = Math.min(Math.max(rect.width, 270), window.innerWidth - 16);
      menu.style.width = `${width}px`;
      menu.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`;
      const availableBelow = window.innerHeight - rect.bottom - 8;
      const availableAbove = rect.top - 8;
      const above = availableBelow < 170 && availableAbove > availableBelow;
      menu.style.maxHeight = `${Math.min(340, Math.max(100, above ? availableAbove : availableBelow))}px`;
      if (above) {
        menu.style.top = 'auto';
        menu.style.bottom = `${Math.max(8, window.innerHeight - rect.top + 4)}px`;
      } else {
        menu.style.bottom = 'auto';
        menu.style.top = `${Math.min(window.innerHeight - 50, rect.bottom + 4)}px`;
      }
    }

    function mark(index) {
      if (index < 0 || index >= choices.length) return;
      active = index;
      optionButtons.forEach((button, i) => {
        const selected = i === active;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-selected', String(selected));
      });
      input.setAttribute('aria-activedescendant', optionButtons[index].id);
      optionButtons[index].scrollIntoView({ block: 'nearest' });
    }

    function selectAirport(record) {
      isSelecting = true;
      input.value = airportLabel(record, input.maxLength);
      // Inform MVEC's EXISTING live calculation listener; do not change its formulas.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      isSelecting = false;
      close();
      input.focus();
    }

    function show() {
      if (isSelecting) return;
      const query = input.value.trim();
      if (query.length < 2) {
        close();
        return;
      }
      try {
        choices = catalog.search(query, 8);
        if (!Array.isArray(choices)) choices = [];
      } catch {
        choices = [];
      }
      menu.replaceChildren();
      optionButtons = [];
      active = -1;
      input.removeAttribute('aria-activedescendant');

      if (!choices.length) {
        const empty = document.createElement('div');
        empty.className = 'va-mvec-airport-empty';
        empty.textContent = copy[1];
        menu.appendChild(empty);
      }

      choices.forEach((record, index) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'va-mvec-airport-option';
        option.id = `${id}-option-${index}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        const primary = document.createElement('span');
        primary.className = 'va-mvec-airport-primary';
        primary.textContent = [record.city, record.airport].filter(Boolean).join(' — ');
        const secondary = document.createElement('span');
        secondary.className = 'va-mvec-airport-secondary';
        secondary.textContent = [record.iata, record.icao, record.country].filter(Boolean).join(' · ');
        option.append(primary, secondary);
        option.addEventListener('mousedown', event => event.preventDefault());
        option.addEventListener('click', () => selectAirport(record));
        menu.appendChild(option);
        optionButtons.push(option);
      });

      const info = document.createElement('div');
      info.className = 'va-mvec-airport-info';
      info.textContent = copy[2];
      menu.appendChild(info);

      // Only one drop-down may be open at a time.
      for (const otherClose of [...OPEN_LISTS]) if (otherClose !== close) otherClose();
      menu.hidden = false;
      OPEN_LISTS.add(close);
      input.setAttribute('aria-expanded', 'true');
      place();
    }

    input.addEventListener('input', show);
    input.addEventListener('focus', show);
    input.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key === 'Tab') {
        close();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (menu.hidden) show();
        if (!choices.length) return;
        event.preventDefault();
        const next = event.key === 'ArrowDown' ? (active + 1) % choices.length
          : (active + choices.length - 1) % choices.length;
        mark(next);
      } else if (event.key === 'Enter' && !menu.hidden && choices.length) {
        event.preventDefault();
        selectAirport(choices[active >= 0 ? active : 0]);
      }
    });

    document.addEventListener('pointerdown', event => {
      if (!menu.hidden && event.target !== input && !menu.contains(event.target)) close();
    });
    document.addEventListener('focusin', event => {
      if (!menu.hidden && event.target !== input && !menu.contains(event.target)) close();
    });
    // A scroll may move the input: anchor the menu in the correct position.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
  }

  function init() {
    const catalog = window.VarelonAirportSearch;
    if (!catalog || typeof catalog.search !== 'function') {
      console.warn('VARELON MVEC: airport catalogue is not loaded.');
      return;
    }
    ensureStyles();
    document.querySelectorAll('.va-mvec[data-varelon-mvec]').forEach(root => {
      for (const key of ['origin', 'destination']) {
        const input = root.querySelector(`[data-mvec-id="${key}"]`);
        if (input instanceof HTMLInputElement) setup(input, root, catalog);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();